import React, { useMemo, useState } from 'react'
import { X, MapPin, Calendar, Clock, Phone, User, Package, AlertTriangle } from 'lucide-react'
import { useUser } from '../contexts/UserContext'

interface PickupRequestFormProps {
  onClose: () => void
  onSubmit: (formData: PickupRequestData) => void
  isSubmitting?: boolean
  userType?: 'FOOD_BANK' | 'SUPPLIER' | 'VOLUNTEER'
}

export interface PickupRequestData {
  title: string
  description: string
  pickupAddress: string
  estimatedWeight: string
  urgency: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT'
  scheduledDate: string
  scheduledTime: string
  specialInstructions: string
  contactName: string
  contactPhone: string
  supplierName: string
  supplierPhone: string
  foodCategories: string[]
  // Address fields for donations
  pickupStreet: string
  pickupCity: string
  pickupState: string
  pickupZipCode: string
  pickupCountry?: string
  pickupLatitude?: number
  pickupLongitude?: number
  deliveryStreet?: string
  deliveryCity?: string
  deliveryState?: string
  deliveryZipCode?: string
  deliveryCountry?: string
  deliveryLatitude?: number
  deliveryLongitude?: number
  pickupTime?: string
  items?: any[]
  expiresAt?: string
}

const PickupRequestForm: React.FC<PickupRequestFormProps> = ({ onClose, onSubmit, isSubmitting = false, userType = 'SUPPLIER' }) => {
  const { user } = useUser()
  const isFoodBank = userType === 'FOOD_BANK'

  const [formData, setFormData] = useState<PickupRequestData>({
    title: '',
    description: '',
    pickupAddress: '',
    estimatedWeight: '',
    urgency: 'MEDIUM',
    scheduledDate: '',
    scheduledTime: '',
    specialInstructions: '',
    contactName: '',
    contactPhone: '',
    supplierName: '',
    supplierPhone: '',
    foodCategories: [],
    pickupStreet: '',
    pickupCity: '',
    pickupState: '',
    pickupZipCode: '',
    pickupCountry: 'USA',
    deliveryStreet: '',
    deliveryCity: '',
    deliveryState: '',
    deliveryZipCode: '',
    deliveryCountry: 'USA',
    pickupTime: '',
    expiresAt: '',
    items: []
  })

  const [errors, setErrors] = useState<Partial<PickupRequestData>>({})

  const foodBankAddress = useMemo(() => {
    if (!user) return ''
    return [user.street, user.city, user.state, user.zipCode].filter(Boolean).join(', ')
  }, [user])

  const fullAddress = useMemo(() => {
    const parts = [formData.pickupStreet, formData.pickupCity, formData.pickupState, formData.pickupZipCode].filter(Boolean)
    return parts.join(', ')
  }, [formData.pickupStreet, formData.pickupCity, formData.pickupState, formData.pickupZipCode])

  const urgencyOptions = [
    { value: 'LOW', label: 'Low', color: 'text-green-600', description: 'Regular pickup, flexible timing' },
    { value: 'MEDIUM', label: 'Medium', color: 'text-yellow-600', description: 'Preferred within 2-3 days' },
    { value: 'HIGH', label: 'High', color: 'text-orange-600', description: 'Needed within 24-48 hours' },
    { value: 'URGENT', label: 'Urgent', color: 'text-red-600', description: 'Critical - needed today' }
  ]

  const foodCategoryOptions = [
    'Fresh Produce', 'Non-Perishable Foods', 'Dairy & Eggs', 'Bakery Items',
    'Protein Items', 'Beverages', 'Baby & Child Items', 'Personal Care'
  ]

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    // Clear error when user starts typing
    if (errors[name as keyof PickupRequestData]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  const handleCategoryChange = (category: string) => {
    setFormData(prev => ({
      ...prev,
      foodCategories: prev.foodCategories.includes(category)
        ? prev.foodCategories.filter(c => c !== category)
        : [...prev.foodCategories, category]
    }))
  }

  const validateForm = (): boolean => {
    const newErrors: Partial<PickupRequestData> = {}

    if (!formData.title.trim()) newErrors.title = 'Title is required'
    if (!formData.description.trim()) newErrors.description = 'Description is required'
    if (!formData.estimatedWeight.trim()) newErrors.estimatedWeight = 'Estimated weight is required'

    if (isFoodBank) {
      if (!user?.street || !user?.city || !user?.state || !user?.zipCode) {
        ;(newErrors as Record<string, string>).pickupStreet =
          'Your food bank address is missing. Complete onboarding before requesting supplies.'
      }
      const phone = user?.phone?.trim() || ''
      if (!phone || phone.startsWith('__pending__')) {
        ;(newErrors as Record<string, string>).contactPhone =
          'Your food bank phone is missing. Complete onboarding before requesting supplies.'
      }
      if (formData.expiresAt) {
        const exp = new Date(formData.expiresAt)
        if (Number.isNaN(exp.getTime()) || exp.getTime() <= Date.now()) {
          ;(newErrors as Record<string, string>).expiresAt = 'Listing expiration must be in the future'
        }
      }
    } else {
      if (!formData.scheduledDate) newErrors.scheduledDate = 'Scheduled date is required'
      if (!formData.scheduledTime) newErrors.scheduledTime = 'Scheduled time is required'
      if (!formData.contactName.trim()) newErrors.contactName = 'Contact name is required'
      if (!formData.contactPhone.trim()) newErrors.contactPhone = 'Contact phone is required'
      if (!formData.pickupStreet.trim()) newErrors.pickupStreet = 'Street is required'
      if (!formData.pickupCity.trim()) newErrors.pickupCity = 'City is required'
      if (!formData.pickupState.trim()) newErrors.pickupState = 'State is required'
      if (!formData.pickupZipCode.trim()) newErrors.pickupZipCode = 'ZIP code is required'
      if (!formData.supplierName.trim()) newErrors.supplierName = 'Supplier name is required'
      if (!formData.supplierPhone.trim()) newErrors.supplierPhone = 'Supplier phone is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    if (isFoodBank && user) {
      const contactName =
        user.organizationName?.trim() ||
        `${user.firstName || ''} ${user.lastName || ''}`.trim() ||
        'Food Bank Contact'
      const contactPhone = user.phone.trim()

      onSubmit({
        ...formData,
        contactName,
        contactPhone,
        pickupStreet: user.street || '',
        pickupCity: user.city || '',
        pickupState: user.state || '',
        pickupZipCode: user.zipCode || '',
        pickupCountry: 'USA',
        pickupAddress: foodBankAddress,
        specialInstructions: '',
        scheduledDate: '',
        scheduledTime: '',
        pickupTime: undefined,
        foodCategories: []
      })
      return
    }

    const pickupAddress = fullAddress || formData.pickupAddress
    const pickupTime = formData.scheduledDate && formData.scheduledTime
      ? `${formData.scheduledDate}T${formData.scheduledTime}`
      : formData.pickupTime

    onSubmit({
      ...formData,
      pickupAddress,
      pickupTime
    })
  }

  const getMinDate = () => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  }

  const getMinDateTime = () => {
    const now = new Date()
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset())
    return now.toISOString().slice(0, 16)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">
            {isFoodBank ? 'Request Supplies' : 'Request Food Pickup'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Basic Information */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="lg:col-span-2">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
            </div>
            
            <div>
              <label htmlFor="title" className="form-label">
                {isFoodBank ? 'Supply Request Title' : 'Pickup Title'} *
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                className={`input-field ${errors.title ? 'border-red-500' : ''}`}
                placeholder={isFoodBank ? 'e.g., General food supplies needed' : 'e.g., Weekly produce pickup from local farm'}
              />
              {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
            </div>

            <div>
              <label htmlFor="estimatedWeight" className="form-label">
                Estimated Weight/Volume *
              </label>
              <input
                type="text"
                id="estimatedWeight"
                name="estimatedWeight"
                value={formData.estimatedWeight}
                onChange={handleChange}
                className={`input-field ${errors.estimatedWeight ? 'border-red-500' : ''}`}
                placeholder="e.g., 50 lbs, 3 boxes, 20 bags"
              />
              {errors.estimatedWeight && <p className="text-red-500 text-sm mt-1">{errors.estimatedWeight}</p>}
            </div>

            <div className="lg:col-span-2">
              <label htmlFor="description" className="form-label">
                Description *
              </label>
              <textarea
                id="description"
                name="description"
                rows={isFoodBank ? 5 : 3}
                value={formData.description}
                onChange={handleChange}
                className={`input-field ${errors.description ? 'border-red-500' : ''}`}
                placeholder={
                  isFoodBank
                    ? 'Describe what you need, preferred delivery/pickup schedule, and any notes for volunteers (alternate drop-off site, special hours, etc.).'
                    : 'Describe the types of food items, any special handling requirements, etc.'
                }
              />
              {isFoodBank && (
                <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                  Include scheduling in this description (preferred days/times). Also tell volunteers about an
                  alternate drop-off site, special hours, or anything else they should know.
                </p>
              )}
              {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
            </div>
          </div>

          {/* Supplier Information - Only show for non-food bank users */}
          {!isFoodBank && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="lg:col-span-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Supplier Information</h3>
              </div>
              
              <div>
                <label htmlFor="supplierName" className="form-label">
                  <User className="inline h-4 w-4 mr-1" />
                  Supplier Name *
                </label>
                <input
                  type="text"
                  id="supplierName"
                  name="supplierName"
                  value={formData.supplierName}
                  onChange={handleChange}
                  className={`input-field ${errors.supplierName ? 'border-red-500' : ''}`}
                  placeholder="Name of restaurant, store, or farm"
                />
                {errors.supplierName && <p className="text-red-500 text-sm mt-1">{errors.supplierName}</p>}
              </div>

              <div>
                <label htmlFor="supplierPhone" className="form-label">
                  <Phone className="inline h-4 w-4 mr-1" />
                  Supplier Phone *
                </label>
                <input
                  type="tel"
                  id="supplierPhone"
                  name="supplierPhone"
                  value={formData.supplierPhone}
                  onChange={handleChange}
                  className={`input-field ${errors.supplierPhone ? 'border-red-500' : ''}`}
                  placeholder="(555) 123-4567"
                />
                {errors.supplierPhone && <p className="text-red-500 text-sm mt-1">{errors.supplierPhone}</p>}
              </div>

              <div className="lg:col-span-2">
                <label className="form-label">
                  <MapPin className="inline h-4 w-4 mr-1" />
                  Pickup Address *
                </label>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div className="lg:col-span-2">
                    <label htmlFor="pickupStreet" className="sr-only">Street</label>
                    <input
                      type="text"
                      id="pickupStreet"
                      name="pickupStreet"
                      value={formData.pickupStreet}
                      onChange={handleChange}
                      className={`input-field ${errors.pickupStreet ? 'border-red-500' : ''}`}
                      placeholder="Street address"
                      required
                    />
                    {errors.pickupStreet && <p className="text-red-500 text-sm mt-1">{errors.pickupStreet}</p>}
                  </div>
                  <div>
                    <label htmlFor="pickupCity" className="sr-only">City</label>
                    <input
                      type="text"
                      id="pickupCity"
                      name="pickupCity"
                      value={formData.pickupCity}
                      onChange={handleChange}
                      className={`input-field ${errors.pickupCity ? 'border-red-500' : ''}`}
                      placeholder="City"
                      required
                    />
                    {errors.pickupCity && <p className="text-red-500 text-sm mt-1">{errors.pickupCity}</p>}
                  </div>
                  <div>
                    <label htmlFor="pickupState" className="sr-only">State</label>
                    <input
                      type="text"
                      id="pickupState"
                      name="pickupState"
                      value={formData.pickupState}
                      onChange={handleChange}
                      className={`input-field ${errors.pickupState ? 'border-red-500' : ''}`}
                      placeholder="State"
                      required
                    />
                    {errors.pickupState && <p className="text-red-500 text-sm mt-1">{errors.pickupState}</p>}
                  </div>
                  <div>
                    <label htmlFor="pickupZipCode" className="sr-only">ZIP</label>
                    <input
                      type="text"
                      id="pickupZipCode"
                      name="pickupZipCode"
                      value={formData.pickupZipCode}
                      onChange={handleChange}
                      className={`input-field ${errors.pickupZipCode ? 'border-red-500' : ''}`}
                      placeholder="ZIP code"
                      required
                    />
                    {errors.pickupZipCode && <p className="text-red-500 text-sm mt-1">{errors.pickupZipCode}</p>}
                  </div>
                  {fullAddress && (
                    <div className="lg:col-span-2">
                      <p className="text-xs text-gray-500">Full address: {fullAddress}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Food bank pickup location (from profile) */}
          {isFoodBank && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Pickup Location</h3>
              <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-700">
                <p className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 mt-0.5 text-primary-600 shrink-0" />
                  <span>
                    Supplies are picked up / dropped off at your food bank address
                    {foodBankAddress ? (
                      <>: <span className="font-medium text-gray-900">{foodBankAddress}</span></>
                    ) : (
                      <> (complete onboarding to set your address).</>
                    )}
                  </span>
                </p>
                <p className="mt-2 text-xs text-gray-500">
                  Contact on the listing will use your food bank profile name and phone.
                </p>
              </div>
              {(errors as Record<string, string>).pickupStreet && (
                <p className="text-red-500 text-sm mt-2">{(errors as Record<string, string>).pickupStreet}</p>
              )}
              {(errors as Record<string, string>).contactPhone && (
                <p className="text-red-500 text-sm mt-1">{(errors as Record<string, string>).contactPhone}</p>
              )}
            </div>
          )}

          {/* Food Categories - suppliers only */}
          {!isFoodBank && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Food Categories</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {foodCategoryOptions.map((category) => (
                  <label key={category} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.foodCategories.includes(category)}
                      onChange={() => handleCategoryChange(category)}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span className="text-sm text-gray-700">{category}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Priority + listing expiration (food banks); full scheduling for others */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-3">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {isFoodBank ? 'Priority & Listing' : 'Scheduling & Priority'}
              </h3>
            </div>

            {!isFoodBank && (
              <>
                <div>
                  <label htmlFor="scheduledDate" className="form-label">
                    <Calendar className="inline h-4 w-4 mr-1" />
                    Preferred Date *
                  </label>
                  <input
                    type="date"
                    id="scheduledDate"
                    name="scheduledDate"
                    min={getMinDate()}
                    value={formData.scheduledDate}
                    onChange={handleChange}
                    className={`input-field ${errors.scheduledDate ? 'border-red-500' : ''}`}
                  />
                  {errors.scheduledDate && <p className="text-red-500 text-sm mt-1">{errors.scheduledDate}</p>}
                </div>

                <div>
                  <label htmlFor="scheduledTime" className="form-label">
                    <Clock className="inline h-4 w-4 mr-1" />
                    Preferred Time *
                  </label>
                  <input
                    type="time"
                    id="scheduledTime"
                    name="scheduledTime"
                    value={formData.scheduledTime}
                    onChange={handleChange}
                    className={`input-field ${errors.scheduledTime ? 'border-red-500' : ''}`}
                  />
                  {errors.scheduledTime && <p className="text-red-500 text-sm mt-1">{errors.scheduledTime}</p>}
                </div>
              </>
            )}

            <div>
              <label htmlFor="urgency" className="form-label">
                <AlertTriangle className="inline h-4 w-4 mr-1" />
                Priority Level
              </label>
              <select
                id="urgency"
                name="urgency"
                value={formData.urgency}
                onChange={handleChange}
                className="input-field"
              >
                {urgencyOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label} - {option.description}
                  </option>
                ))}
              </select>
            </div>

            {isFoodBank && (
              <div className="lg:col-span-2">
                <label htmlFor="expiresAt" className="form-label">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Listing expires (optional)
                </label>
                <input
                  type="datetime-local"
                  id="expiresAt"
                  name="expiresAt"
                  min={getMinDateTime()}
                  value={formData.expiresAt}
                  onChange={handleChange}
                  className={`input-field ${(errors as Record<string, string>).expiresAt ? 'border-red-500' : ''}`}
                />
                {(errors as Record<string, string>).expiresAt && (
                  <p className="text-red-500 text-sm mt-1">{(errors as Record<string, string>).expiresAt}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  After this date and time, the request is removed from public listings. You can also take it down anytime.
                </p>
              </div>
            )}
          </div>

          {/* Contact Information - non-food-bank only */}
          {!isFoodBank && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="lg:col-span-2">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Your Contact Information</h3>
              </div>
              
              <div>
                <label htmlFor="contactName" className="form-label">
                  <User className="inline h-4 w-4 mr-1" />
                  Your Name *
                </label>
                <input
                  type="text"
                  id="contactName"
                  name="contactName"
                  value={formData.contactName}
                  onChange={handleChange}
                  className={`input-field ${errors.contactName ? 'border-red-500' : ''}`}
                  placeholder="Your name"
                />
                {errors.contactName && <p className="text-red-500 text-sm mt-1">{errors.contactName}</p>}
              </div>

              <div>
                <label htmlFor="contactPhone" className="form-label">
                  <Phone className="inline h-4 w-4 mr-1" />
                  Your Phone *
                </label>
                <input
                  type="tel"
                  id="contactPhone"
                  name="contactPhone"
                  value={formData.contactPhone}
                  onChange={handleChange}
                  className={`input-field ${errors.contactPhone ? 'border-red-500' : ''}`}
                  placeholder="(555) 123-4567"
                />
                {errors.contactPhone && <p className="text-red-500 text-sm mt-1">{errors.contactPhone}</p>}
              </div>

              <div className="lg:col-span-2">
                <label htmlFor="specialInstructions" className="form-label">
                  Special Instructions
                </label>
                <textarea
                  id="specialInstructions"
                  name="specialInstructions"
                  rows={3}
                  value={formData.specialInstructions}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="Any special handling requirements, access instructions, etc."
                />
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline flex-1 sm:flex-none"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1 sm:flex-none"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Submitting...
                </>
              ) : (
                <>
                  <Package className="h-4 w-4 mr-2" />
                  {isFoodBank ? 'Request Supplies' : 'Request Pickup'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default PickupRequestForm
